import React, { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { createRoot } from '@wordpress/element';
import { Tab, TabsList, TabPanel, Tabs } from '@mui/base'; // https://mui.com/base-ui/react-tabs/
import { MatchBox, LiveBtn, Lineup, MatchInfoWidget, GameStatsWidget, Sidebar, GameEvents } from './components';
import { getConnectionInfo, getEndpoint, getDate, MatchContext, ConnectionContext, displayErrorMessage, displayLoader } from './utils';
import { getStoredData } from "./utils/data-pulls";


const MatchApp = (props) => {
    const { 
        matchId = '',
        postId = '',
        ...restProps 
    } = props;

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);

    // Connection Information
    const [connectionInfo, setConnectionInfo] = useState(null);

    // Setting data from API
    const [data, setData] = useState(null);
    const [ isFallback, setIsFallback ] = useState(false);
    const [events, setEvents] = useState(null);
    const [awayLineup, setAwayLineup] = useState(null);
    const [homeLineup, setHomeLineup] = useState(null);

    // Data pieces
    const [lastUpdatedTime, setLastUpdatedTime] = useState(null);
    const [ awayTeamName, setAwayTeamName ] = useState('');
    const [ homeTeamName, setHomeTeamName ] = useState('');
    const [ matchTitle, setMatchTitle ] = useState('');
    const [ isMatchLive, setMatchLive ] = useState(false);
    const [ refreshCounter, setRefreshCounter ] = useState(0);
    const [ matchDate, setMatchDate] = useState(null);
    const [ showEvents, setShowEvents ] = useState(true);
    const [ stats, setStats ] = useState(null);

    // Layout pieces
    const [ selectedTab, setSelectedTab ] = useState(1);


    /**
     * Get our connection info
     */
    useEffect(() => {

        // Get our connection info
        getConnectionInfo().then(
            (result) => {
                setConnectionInfo(result.data);
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

      }, []);


    /**
     * Connect to the API and get our data
     * If the match is live, refresh based on our refresh counter
     */
    useEffect(() => {
        if ( !connectionInfo ) return;

        // Get main match data
        const matchEndpoint = getEndpoint('match', {
            matchId: matchId,
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id
        }, connectionInfo.environment);
        
        fetch(matchEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
          .then(res => res.json())
          .then(
            (result) => {
              if ( result.hasOwnProperty('messages') ) {
                setData(null);
                setIsFallback(true);
              } else {
                setData(result);
                setIsLoaded(true);
              }
            },
            (error) => {
              setIsLoaded(true);
              setError(error);
              console.log(error);
            }
          );

          // Get match event data
        const eventsEndpoint = getEndpoint('match_events', {
            matchId: matchId,
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id
        }, connectionInfo.environment);
        fetch(eventsEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
          .then(res => res.json())
          .then(
            (result) => {
                setEvents(result);
            },
            (error) => {
              setError(error);
              console.log(error);
            }
          );

        // Get match lineup data
        const lineupEndpoint = getEndpoint('match_lineups', {
            matchId: matchId,
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id
        }, connectionInfo.environment);
        fetch(lineupEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
          .then(res => res.json())
          .then(
            (result) => {
                // Check if there's any lineup info to display
                const home = result.hasOwnProperty('home') && (result.home.players.length || result.home.officials.length) ? result.home : null;
                const away = result.hasOwnProperty('away') && (result.away.players.length || result.away.officials.length) ? result.away : null;
                setHomeLineup( home );
                setAwayLineup( away );
            },
            (error) => {
              setError(error);
              console.log(error);
            }
          );

            

      }, [connectionInfo, refreshCounter]);


      /**
       * Setup Fallback: Pull what data we have from the website
       */
      useEffect(() => {
        if ( !isFallback ) return;

        setMatchTitle('Planned Match');

        let args = [
            { 
                key: 'match',
                id: postId
            }
        ];

        // Get our stored matches
        getStoredData(args).then(
            (result) => {

                // Set match data
                if ( result.data.match ) {
                    setData(result.data.match);
                } else {
                    
                }

                // Set loaded to true
                setIsLoaded(true);
                
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

      }, [isFallback]);


    /**
     * Setup Match Basic Info
     */
    useEffect(() => {
        if ( !data ) return;

        switch (data.liveStatus) {
            case 'PLAYED':
                setMatchTitle(`PAST Match`);
                break;
            case 'RUNNING':
                setMatchTitle(`LIVE Match`);
                setMatchLive(true);
                break;
            case 'SCHEDULED':
            case 'POSTPONED':
            case 'CANCELED':
                setMatchTitle(`${data.liveStatus} Match`);
                setShowEvents(false);
                setSelectedTab(2);
            case 'PLANNED':
                setMatchTitle(`${data.liveStatus} Match`);
                setShowEvents(false);
                setSelectedTab(2);
                break;
            default:
                setMatchTitle(`${data.liveStatus} Match`);
                break;
        }

        setMatchDate('dateTimeUTC' in data && data.dateTimeUTC ? getDate(data.dateTimeUTC, 'long') : 'Date To Be Determined');

        setAwayTeamName( 'awayTeam' in data && data.awayTeam && 'name' in data.awayTeam ? data.awayTeam.name : '' );
        setHomeTeamName( 'homeTeam' in data && data.homeTeam && 'name' in data.homeTeam ? data.homeTeam.name : '' );

    }, [data]);


    /**
     * Handle tab changes
     */
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };


    /**
     * Setup match stats
     */
    useEffect(() => {
        if ( !events || !events.length ) return;

        let tempStats = [
            {
                name: 'Goals',
                slug: 'goals',
                away: 0,
                home: 0
            },
            {
                name: 'Yellow Cards',
                slug: 'yellowCards',
                away: 0,
                home: 0
            },
            {
                name: 'Red Cards',
                slug: 'redCards',
                away: 0,
                home: 0
            },
            {
                name: 'Substitutions',
                slug: 'subs',
                away: 0,
                home: 0
            }
        ];
        events.map((event) => {
            if ( 'eventType' in event && 'fcdName' in event.eventType ) {
                switch (event.eventType.fcdName) {
                    case 'GOAL':
                    case 'PENALTY':
                        if ( event.homeTeam ) {  tempStats[0].home++ } else { tempStats[0].away++ }
                        break;
                    case 'OWN_GOAL':
                        if ( event.homeTeam ) {  tempStats[0].away++ } else { tempStats[0].home++ }
                        break;
                    case 'YELLOW':
                    case 'SECOND_YELLOW':
                        if ( event.homeTeam ) {  tempStats[1].home++ } else { tempStats[1].away++ }
                        break;
                    case 'RED':
                        if ( event.homeTeam ) {  tempStats[2].home++ } else { tempStats[2].away++ }
                        break;
                    case 'SUBSTITUTION':
                        if ( event.homeTeam ) {  tempStats[3].home++ } else { tempStats[3].away++ }
                        break;
                }
            }
        });
        setStats(tempStats);
    


    }, [ events ]);


    /**
     * Setup countdown to refresh live matches
     */
    useEffect(() => {
        if ( !isMatchLive ) return;

        const refreshRate = 60; // Every 60 seconds

        setTimeout(() => {
            setRefreshCounter(a => a + 1);
        }, refreshRate * 1000);
      
      }, [isMatchLive, refreshCounter]);


    /**
     * Update 'last updated' ticker
     */
    useEffect(()=>{
        if ( !isMatchLive ) return;
        let secondsSinceUpdate = 0;
        const refreshRate = 10; // Time in seconds

        setLastUpdatedTime('now');

        const interval = setInterval(() => {
            secondsSinceUpdate += 1;
            const secondsStr = secondsSinceUpdate == 1 ? ' second ago' : ' seconds ago';
            setLastUpdatedTime(secondsSinceUpdate + secondsStr);
        }, refreshRate * 100);

        return () => clearInterval(interval);
    }, [isMatchLive, refreshCounter]);



    // Bail if we don't have data yet
    if (!isLoaded ) {
        return displayLoader('acaLoader--main');
    }

    // Display an error message if there's a problem
    if ( error ) {
        console.log(error);
        return displayErrorMessage();
    }

    return (
        <ConnectionContext.Provider value={connectionInfo}>
            <MatchContext.Provider value={{data, setData}}>
                <section className="acaPage acaPage--withSidebar acaPage--match">

                    <div className="acaMain">

                        { /** Match Header */ }
                        <div className="matchHeader">
                            <span className="acaH4 matchDate">{matchDate} {isMatchLive ? (<LiveBtn />) : ''}</span>
                            <h1 className="acaH1--alt">{matchTitle}</h1>
                            { isMatchLive ? (
                                <p className="acaSmallNote matchLastUpdated">{`Last updated ${lastUpdatedTime}`}</p>
                            ) : '' }
                        </div>
                        { awayTeamName && homeTeamName ? (
                            <ErrorBoundary fallback={displayErrorMessage()}>
                                <MatchBox includeLink={false} data={data} />
                            </ErrorBoundary>
                        ) : '' }

                        { /** Fallback message */ }
                        { isFallback ? (
                            displayErrorMessage('No match information is available yet. Check back later.')
                        ) : '' }

                        { /** Tabbed Layout */ }
                        { showEvents || awayLineup || homeLineup ? (
                            <Tabs className="acaSmallTabs acaSmallTabs--center" value={ selectedTab }  onChange={handleTabChange}>
                                <TabsList>
                                    { showEvents ? ( <Tab value={1}>Game Summary</Tab> ) : '' }
                                    { awayLineup || homeLineup ? ( <Tab value={2}>Lineup</Tab> ) : '' }
                                </TabsList>

                                { /** Match Events */ }
                                { showEvents ? (
                                <TabPanel value={1}>
                                    <ErrorBoundary fallback={displayErrorMessage()}>
                                        <GameEvents events={events} />
                                    </ErrorBoundary>
                                    { isMatchLive ? (
                                        <p className="acaSmallNote matchEventsUpdatedTime">{`Last updated ${lastUpdatedTime}`}</p>
                                    ) : '' }
                                </TabPanel>
                                ) : '' }

                                { /** Lineups */ }
                                { awayLineup || homeLineup ? (
                                    <TabPanel value={2}>
                                        <ErrorBoundary fallback={displayErrorMessage()}> 
                                            <div className="matchLineups">
                                                { homeLineup ? (
                                                    <Lineup 
                                                        lineup={homeLineup} 
                                                        side="home"
                                                        teamName={homeTeamName} 
                                                        includeTeamName={true}
                                                    />
                                                ) : ( displayErrorMessage('No home team lineup available yet.') ) }
                                                { awayLineup ? (
                                                    <Lineup 
                                                        lineup={awayLineup} 
                                                        side="away"
                                                        teamName={awayTeamName} 
                                                        includeTeamName={true}
                                                    />
                                                ) : ( displayErrorMessage('No away team lineup available yet.') ) }
                                            </div>
                                        </ErrorBoundary>
                                    </TabPanel>
                                ) : '' }
                            </Tabs>
                        ) : ''  }
                        
                    </div>

                    { /** Sidebar */ }
                    <Sidebar soccerImg>
                        { data ? (
                            <>
                                <ErrorBoundary fallback={displayErrorMessage()}>
                                    <MatchInfoWidget 
                                        competition={ 'competition' in data && data.competition && 'name' in data.competition ? data.competition.name : '' }
                                        competitionUrl="#" 
                                        venue={ 'facility' in data && data.facility && 'name' in data.facility ? data.facility.name : '' } 
                                        attendance={ 'attendance' in data && data.attendance ? data.attendance : '' }
                                    />
                                </ErrorBoundary>
                                <ErrorBoundary fallback={displayErrorMessage()}>
                                    <GameStatsWidget 
                                        awayName = {awayTeamName}
                                        homeName = {homeTeamName}
                                        stats = {stats}
                                    />
                                </ErrorBoundary>
                            </>
                        ) : '' }
                    </Sidebar>

                </section>
            </MatchContext.Provider>
        </ConnectionContext.Provider>
    );
}

export default MatchApp;

// Render Match Page
const appContainer = document.getElementById('matchApp');
const root = createRoot(appContainer);
const matchId = appContainer.dataset.id;
const postId = appContainer.dataset.postId;
root.render(<MatchApp matchId={matchId} postId={postId} />);